<template>
    <div class="SaleOptionAnonymousFareMediaChoice">
        <alert-message
            v-if="wrongFareMediaErrorMessage !== null"
            :message="wrongFareMediaErrorMessage"
            class="mb-6"
            type="error"
        />
        <v-text-field
            :hide-details="true"
            :label="$t('sale_option:card_number')"
            :model-value="selectedFareMedia"
            :variant="getVariant"
            class="SaleOptionAnonymousFareMediaChoice-field"
            density="compact"
            @update:model-value="fareMediaUpdated"
        />
        <div
            v-if="$te('how_to_write_card_number:link')"
            class="SaleOptionAnonymousFareMediaChoice-howToWriteCardNumber"
        >
            <a
                class="SaleOptionAnonymousFareMediaChoice-howToWriteCardNumberLink font-italic"
                :href="$t('how_to_write_card_number:link')"
                target="_blank"
            >
                {{ $t('how_to_write_card_number:description') }}
            </a>
        </div>
        <v-btn
            :disabled="isSubmitButtonDisabled"
            class="SaleOptionAnonymousFareMediaChoice-validateButton"
            color="primary"
            size="large"
            variant="flat"
            @click.stop="updateFareMedia"
        >
            {{ $t('button:validate') }}
        </v-btn>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { isEmpty } from 'global-utils'
import config from 'config'
import { useI18n } from 'vue-i18n'
import AlertMessage from '@/StoreWeb/components/common/AlertMessage'
import { checkAndGetFareMedia, isValidFareMediaId } from '@/StoreWeb/js/mixins/faremedia-utils'

const store = useStore()
const { t } = useI18n()

const emit = defineEmits(['anonymousFareMediaUpdated'])

const props = defineProps({
    provider: {
        type: String,
        default: ''
    }
})
const selectedFareMedia = ref('')
const wrongFareMediaErrorMessage = ref(null)

const isMobileApp = computed(() => store.state.isMobileApp)
const applicationSession = computed(() => store.state.applicationSessionModule.applicationSession)
const getVariant = computed(() => config?.theme?.inputs_variant ?? null)
const isSubmitButtonDisabled = computed(() => {
    return !selectedFareMedia.value || !isValidFareMediaId(selectedFareMedia.value, props.provider)
})

const fareMediaUpdated = (fareMedia) => {
    selectedFareMedia.value = fareMedia

    if (fareMedia && !isValidFareMediaId(fareMedia, props.provider)) {
        wrongFareMediaErrorMessage.value = t('sale_option:card_number:error:wrong_number')
    } else {
        wrongFareMediaErrorMessage.value = null
    }
}

const updateFareMedia = async () => {
    const response = await checkAndGetFareMedia({
        providerId: props.provider,
        fareMediaId: selectedFareMedia.value
    }, false)

    // If there is a technical error, the error will be displayed in the ErrorModal
    if (response.isTechnicalError) {
        return
    }

    if (response.isValid && response?.fareMedia?.id) {
        wrongFareMediaErrorMessage.value = null
        emit('anonymousFareMediaUpdated', {
            basketInfo: 'PROVIDERFAREMEDIA',
            value: {
                fareMediaId: selectedFareMedia.value,
                providerUserExternalId: response.fareMedia.providerUserExternalId
            }
        })
        return
    }

    wrongFareMediaErrorMessage.value = response?.error?.message ?? t('sale_option:card_number:error:wrong_number')
}

onMounted(() => {
    if (
        isMobileApp.value &&
        applicationSession.value &&
        !isEmpty(applicationSession.value.data) &&
        applicationSession.value.data.fareMediaId
    ) {
        selectedFareMedia.value = applicationSession.value.data.fareMediaId
        emit('anonymousFareMediaUpdated', {
            basketInfo: 'PROVIDERFAREMEDIA',
            goToNext: false,
            value: selectedFareMedia.value
        })
        updateFareMedia()
    }
})
</script>

<style lang="scss" scoped>
@import 'globalScss';

.SaleOptionAnonymousFareMediaChoice {
    &-howToWriteCardNumber {
        margin-bottom: $s8;
    }

    &-howToWriteCardNumberLink {
        font-size: $font-xsmall;
        font-style: italic;
        color: $color-black;
    }

    &-buttonsArea {
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
        gap: 15px;
        padding: 0 0 32px;
    }

    &-button,
    &-nfcButton {
        &.v-btn.v-size--large {
            width: 44px;
            min-width: 44px;
        }
    }

    &-button {
        &Icon {
            font-size: 24px;
        }
    }

    &-validateButton {
        width: 100%;
    }
}
</style>
